import { ValidationResult } from './validation-result.model';
import { Card } from '../models/card.model';

export const PARENT_ASSIGNMENT_ERRORS = {
  SELF_PARENT: 'Card cannot be its own parent.',
  CHILD_HAS_CHILDREN: 'Cards with children cannot be assigned to a parent.',
  PARENT_HAS_PARENT: 'A parent card cannot have a parent itself (1-level hierarchy only).',
} as const;

export function validateParentAssignment(
  childCard: Card,
  targetParentCard: Card | null,
  allCards: Card[] = [],
): ValidationResult {
  const errors: string[] = [];

  if (!targetParentCard) {
    return { isValid: true, errors: [] };
  }

  if (childCard.id === targetParentCard.id) {
    errors.push(PARENT_ASSIGNMENT_ERRORS.SELF_PARENT);
  }

  const childHasChildren = allCards.some((candidate) => candidate.parentCardId === childCard.id);
  if (childHasChildren) {
    errors.push(PARENT_ASSIGNMENT_ERRORS.CHILD_HAS_CHILDREN);
  }

  if (targetParentCard.parentCardId !== null) {
    errors.push(PARENT_ASSIGNMENT_ERRORS.PARENT_HAS_PARENT);
  }

  return { isValid: errors.length === 0, errors };
}

export function validateParentHierarchy(cards: Card[]): ValidationResult {
  const errors: string[] = [];

  for (const card of cards) {
    if (!card.parentCardId) {
      continue;
    }

    const targetParent = cards.find((candidate) => candidate.id === card.parentCardId) ?? null;
    if (!targetParent) {
      continue;
    }

    const assignmentResult = validateParentAssignment(card, targetParent, cards);
    if (!assignmentResult.isValid) {
      for (const error of assignmentResult.errors) {
        // The self-parent case is already reported elsewhere (e.g., validateAppState),
        // so we skip it here to avoid duplicate/confusing error messages.
        if (error === PARENT_ASSIGNMENT_ERRORS.SELF_PARENT) {
          continue;
        }

        if (error === PARENT_ASSIGNMENT_ERRORS.PARENT_HAS_PARENT) {
          errors.push(
            `Hierarchy violation: Card ${card.id}'s parent (${targetParent.id}) also has a parent.`,
          );
          continue;
        }

        if (error === PARENT_ASSIGNMENT_ERRORS.CHILD_HAS_CHILDREN) {
          errors.push(`Hierarchy violation: Card ${card.id} cannot be both a child and a parent.`);
          continue;
        }

        errors.push(`Hierarchy violation: ${error}`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors: [...new Set(errors)],
  };
}
